import { computed, ref, toValue, watch } from "vue";
import { getSquareVideoList } from "~/api/api";

/** 模块级共享，避免切详情时组件重挂载导致列表丢失、按钮一直禁用 */
const list = ref([]);
const currentPage = ref(0);
const finished = ref(false);
let locatePromise = null;

const extractShortsList = res => {
	const payload = res?.data && typeof res.data === "object" ? res.data : res || {};
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload.list)) return payload.list;
	if (Array.isArray(payload.data)) return payload.data;
	return [];
};

const getItemId = item => (item?.id != null ? String(item.id) : "");

export function useShortsNavigation(getCurrentId) {
	const getId = () => {
		const id = toValue(getCurrentId);
		return id != null ? String(id) : "";
	};

	const currentIndex = computed(() => list.value.findIndex(item => getItemId(item) === getId()));

	/** 列表有内容且当前不是第一条时，可上一 */
	const hasPrev = computed(() => {
		if (list.value.length === 0) return false;
		const index = currentIndex.value;
		if (index === -1) return list.value.length > 0;
		return index > 0;
	});

	/** 列表有下一条，或未加载完还可翻页 */
	const hasNext = computed(() => {
		if (list.value.length === 0 && finished.value) return false;
		const index = currentIndex.value;
		if (index === -1) return list.value.length > 0 || !finished.value;
		if (index < list.value.length - 1) return true;
		return !finished.value;
	});

	const fetchPage = async (page, append = true) => {
		const res = await getSquareVideoList({
			video_screen_mode: 2,
			page,
		});
		const items = extractShortsList(res);

		if (append) {
			if (items.length) {
				const existingIds = new Set(list.value.map(getItemId));
				const merged = items.filter(item => !existingIds.has(getItemId(item)));
				if (merged.length) list.value = [...list.value, ...merged];
			}
		} else {
			list.value = items;
		}

		currentPage.value = page;
		if (!items.length) finished.value = true;
		return items.length;
	};

	const ensureCurrentLocated = async () => {
		const currentId = getId();
		if (!currentId) return;

		if (currentIndex.value !== -1) return;

		if (locatePromise) {
			await locatePromise;
			return;
		}

		locatePromise = (async () => {
			try {
				if (!list.value.length) {
					finished.value = false;
					await fetchPage(1, false);
				}

				while (currentIndex.value === -1 && !finished.value) {
					const count = await fetchPage(currentPage.value + 1, true);
					if (!count) break;
				}
			} catch (error) {
				console.error("定位 Shorts 列表失败:", error);
			} finally {
				locatePromise = null;
			}
		})();

		await locatePromise;
	};

	const pickFallbackId = direction => {
		const currentId = getId();
		if (!list.value.length) return null;

		if (direction === "next") {
			const found = list.value.find(item => getItemId(item) !== currentId);
			return found?.id ?? null;
		}

		for (let i = list.value.length - 1; i >= 0; i -= 1) {
			const id = getItemId(list.value[i]);
			if (id && id !== currentId) return list.value[i].id;
		}
		return null;
	};

	const getAdjacentId = async direction => {
		await ensureCurrentLocated();

		const index = currentIndex.value;

		// 当前不在列表里：只要列表有其它内容，就切到列表中的上一条/下一条
		if (index === -1) {
			if (!list.value.length && !finished.value) {
				await fetchPage(currentPage.value > 0 ? currentPage.value + 1 : 1, list.value.length > 0);
			}
			return pickFallbackId(direction);
		}

		const targetIndex = direction === "prev" ? index - 1 : index + 1;

		if (targetIndex >= 0 && targetIndex < list.value.length) {
			return list.value[targetIndex]?.id ?? null;
		}

		if (direction === "next" && !finished.value) {
			await fetchPage(currentPage.value + 1, true);
			return list.value[targetIndex]?.id ?? null;
		}

		return null;
	};

	watch(
		getCurrentId,
		() => {
			if (currentIndex.value === -1) {
				ensureCurrentLocated();
			}
		},
		{ immediate: true }
	);

	return {
		hasPrev,
		hasNext,
		getAdjacentId,
	};
}
